import {
  Box,
  Collapse,
  IconButton,
  Stack,
  styled,
  type Theme,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { memo, ReactNode, useMemo } from 'react';
import { Handle, NodeProps, Position } from 'reactflow';
import { DynamicIcon, Tooltip } from 'src/components';
import { WorkflowNodeType } from 'src/pages/Settings/workflows/utils/workflow-definition';
import {
  formatNodeTitle,
  WorkflowNodeDisplayData,
} from 'src/pages/Settings/workflows/utils/workflow-helpers';
import {
  rawCondition,
  STEP_NOT_SET_UP,
  summarizeStep,
} from 'src/pages/Settings/workflows/utils/workflow-step-summary';
import { useWorkflowEditor } from 'src/pages/Settings/workflows/workflow-editor-context';
import { StepTitle } from './step-title';
import { WorkflowStepForm } from './workflow-step-form';

export interface SourceHandleConfig {
  id?: string;
  /** Along the card's leaving edge: from the left down the page, from the top across it. */
  offset?: number | string;
}

interface BaseNodeProps extends NodeProps<WorkflowNodeDisplayData> {
  footer?: ReactNode;
  sourceHandles?: SourceHandleConfig[];
}

const NODE_WIDTH = 280;

const nodeShadow = (
  theme: Theme,
  { open, selected }: { open: boolean; selected: boolean },
) => {
  if (open) return theme.shadows[8];
  if (selected) return theme.shadows[5];
  return theme.shadows[1];
};

const nodeBorderColor = (
  theme: Theme,
  { hasProblem, open, selected }: { hasProblem: boolean; open: boolean; selected: boolean },
) => {
  if (hasProblem) return theme.palette.error.main;
  if (selected || open) return theme.palette.custom.OV.sys.primary;
  return theme.palette.custom.OV.sys.outlineVariant;
};

/** A search match is ringed; the one the search is on gets a wider, softer ring. */
type SearchHighlight = 'none' | 'match' | 'current';

const searchRing = (theme: Theme, highlight: SearchHighlight) => {
  const amber = theme.palette.warning.main;
  if (highlight === 'current') return `0 0 0 3px ${amber}, 0 0 0 9px ${alpha(amber, 0.25)}`;
  if (highlight === 'match') return `0 0 0 3px ${amber}`;
  return null;
};

/** While a step is focused: it gets a ring, its path stays as is, and every other step greys out. */
type FocusState = 'none' | 'focused' | 'onPath' | 'offPath';

const NodeWrapper = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'selected' &&
    prop !== 'hasProblem' &&
    prop !== 'open' &&
    prop !== 'highlight' &&
    prop !== 'focus',
})<{
  selected: boolean;
  hasProblem: boolean;
  open: boolean;
  highlight: SearchHighlight;
  focus: FocusState;
}>(({ theme, selected, hasProblem, open, highlight, focus }) => ({
  opacity: focus === 'offPath' ? 0.3 : 1,
  // the focus button shows on hover, on the selected step, and on the focused one
  '& .workflow-node__focus': { opacity: selected || focus === 'focused' ? 1 : 0 },
  '&:hover .workflow-node__focus, & .workflow-node__focus:focus-visible': { opacity: 1 },
  borderRadius: theme.shape.borderRadius * 2,
  border: `1px solid ${nodeBorderColor(theme, { hasProblem, open, selected })}`,
  outline: hasProblem ? `1px solid ${theme.palette.error.main}` : undefined,
  backgroundColor: theme.palette.custom.OV.sys.surfaceContainerLowest,
  padding: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  width: NODE_WIDTH,
  boxShadow: [
    focus === 'focused' ? `0 0 0 2px ${theme.palette.custom.OV.sys.primary}` : null,
    searchRing(theme, highlight),
    nodeShadow(theme, { open, selected }),
  ]
    .filter(Boolean)
    .join(', '),
  position: 'relative',
  zIndex: selected || open ? 20 : 1,
  cursor: open ? 'default' : 'pointer',
  // opacity changes at once: fading for Focus or a search has to show the moment it's asked for
  transition: theme.transitions.create(['box-shadow', 'transform'], {
    duration: theme.transitions.duration.shorter,
  }),
  '&:hover': open
    ? {}
    : {
        boxShadow: theme.shadows[6],
        transform: 'translateY(-2px)',
      },
  '&:hover .workflow-node__icon': {
    boxShadow: theme.shadows[3],
    transform: 'translateY(-1px) rotate(var(--workflow-icon-rotation, 0deg))',
  },
}));

const Header = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'headerColor',
})<{ headerColor: string }>(({ theme, headerColor }) => ({
  alignItems: 'center',
  flexDirection: 'row',
  justifyContent: 'space-between',
  backgroundColor: headerColor,
  borderRadius: theme.shape.borderRadius,
  paddingInline: theme.spacing(0.75),
  paddingBlock: theme.spacing(0.25),
  columnGap: theme.spacing(1),
}));

const IconWrapper = styled(Stack)(({ theme }) => ({
  width: 28,
  height: 28,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.action.hover,
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  boxShadow: theme.shadows[1],
  transition: theme.transitions.create(['box-shadow', 'transform'], {
    duration: theme.transitions.duration.shorter,
  }),
}));

const DescriptionContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'backgroundColor',
})<{ backgroundColor: string }>(({ theme, backgroundColor }) => ({
  borderRadius: theme.shape.borderRadius,
  backgroundColor,
  padding: theme.spacing(1),
  marginTop: theme.spacing(1),
}));

const hiddenHandleStyle = {
  opacity: 0,
  border: 'none',
} as const;

const BaseNodeComponent = ({ data, selected, footer, sourceHandles }: BaseNodeProps) => {
  const theme = useTheme();
  const { definition, catalog, problemsByNode, editable, openNodeId, setOpenNodeId, view } =
    useWorkflowEditor();
  let highlight: SearchHighlight = 'none';
  if (view.currentMatchId === data.nodeId) highlight = 'current';
  else if (view.matchIds.includes(data.nodeId)) highlight = 'match';
  // Focus lights the way to one step; otherwise an open search lights its matches. Either way,
  // everything else fades.
  let focus: FocusState = 'none';
  if (view.focusedId === data.nodeId) focus = 'focused';
  else if (view.focusedId) focus = view.focusedIds.has(data.nodeId) ? 'onPath' : 'offPath';
  else if (view.searchActive) focus = highlight === 'none' ? 'offPath' : 'onPath';
  const isTrigger = data.nodeType === WorkflowNodeType.Trigger;
  const isOpen = openNodeId === data.nodeId;
  const headerColor = data.headerColor ?? theme.palette.custom.OV.sys.surfaceContainerHigh;
  const summaryBackground = data.accentColor ?? theme.palette.custom.OV.sys.surfaceContainer;
  const detailsTooltip = isOpen ? 'Close step' : 'Open step';

  const node = definition.nodes.find(({ id }) => id === data.nodeId);
  const problems = problemsByNode.get(data.nodeId) ?? [];
  const summary = node ? summarizeStep(node, catalog.byType.get(node.type), problems) : null;
  // a step not set up yet still counts towards "needs attention", but isn't drawn as an error
  const hasProblem = problems.length > 0 && summary !== STEP_NOT_SET_UP;
  let summaryColor = 'text.primary';
  if (hasProblem) summaryColor = 'error.main';
  else if (summary === STEP_NOT_SET_UP) summaryColor = 'text.secondary';
  const condition =
    node && !hasProblem && summary !== STEP_NOT_SET_UP ? rawCondition(node) : null;
  const toggleOpen = () => setOpenNodeId(isOpen ? null : data.nodeId);

  const nodeIconRotationStyle = useMemo(
    () =>
      ({
        '--workflow-icon-rotation': `${Math.random() * 4 - 2}deg`,
      }) as React.CSSProperties,
    [],
  );

  // down the page connections arrive on top and leave from the bottom; across it, left and right
  const targetPosition = data.horizontal ? Position.Left : Position.Top;
  const sourcePosition = data.horizontal ? Position.Right : Position.Bottom;

  const renderSourceHandles = () => {
    if (!sourceHandles?.length) {
      return (
        <Handle
          type="source"
          position={sourcePosition}
          style={hiddenHandleStyle}
          isConnectable={false}
        />
      );
    }

    return sourceHandles.map((handle, index) => (
      <Handle
        key={handle.id ?? `source-${index}`}
        id={handle.id}
        type="source"
        position={sourcePosition}
        style={{
          ...hiddenHandleStyle,
          [data.horizontal ? 'top' : 'left']: handle.offset ?? '50%',
        }}
        isConnectable={false}
      />
    ));
  };

  const description = summary ? (
    <DescriptionContainer backgroundColor={summaryBackground}>
      <Stack direction="row" spacing={0.75} alignItems="flex-start">
        {hasProblem ? (
          <DynamicIcon
            name="error_outline"
            fontSize="small"
            sx={{ color: 'error.main', mt: '1px' }}
          />
        ) : null}
        <Stack minWidth={0}>
          <Typography
            variant="body2"
            color={summaryColor}
            fontWeight={condition ? 600 : undefined}
            sx={{ overflowWrap: 'anywhere' }}
          >
            {summary}
          </Typography>
          {condition ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}
            >
              {condition}
            </Typography>
          ) : null}
        </Stack>
      </Stack>
    </DescriptionContainer>
  ) : null;

  return (
    <NodeWrapper
      focus={focus}
      highlight={highlight}
      selected={selected}
      open={isOpen}
      hasProblem={hasProblem}
      style={nodeIconRotationStyle}
      data-testid={`workflow-step-${data.nodeId}`}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && isOpen) {
          event.stopPropagation();
          setOpenNodeId(null);
        }
      }}
    >
      <Handle
        type="target"
        position={targetPosition}
        style={hiddenHandleStyle}
        isConnectable={false}
      />
      {renderSourceHandles()}
      <Header headerColor={headerColor}>
        <Stack direction="row" spacing={1} alignItems="center" minWidth={0} flex={1}>
          {data.icon ? (
            <IconWrapper className="workflow-node__icon">
              <DynamicIcon
                name={data.icon}
                sx={{
                  color:
                    data.iconColor ??
                    (theme.palette.mode === 'dark'
                      ? theme.palette.common.white
                      : theme.palette.text.primary),
                }}
              />
            </IconWrapper>
          ) : null}
          <StepTitle
            nodeId={data.nodeId}
            name={node?.name ?? ''}
            typeLabel={formatNodeTitle(data.nodeType, catalog)}
            renamable={editable ? selected : false}
          />
        </Stack>
        {isTrigger ? null : (
          <Tooltip title="Focus on this step">
            <IconButton
              size="small"
              className="nodrag workflow-node__focus"
              onClick={(event) => {
                event.stopPropagation();
                view.focusStep(data.nodeId);
              }}
              aria-label="Focus on this step"
            >
              <DynamicIcon name="center_focus_strong" fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={detailsTooltip}>
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              toggleOpen();
            }}
            aria-label={detailsTooltip}
            aria-expanded={isOpen}
          >
            <DynamicIcon
              name="expand_more"
              sx={{
                transition: theme.transitions.create('transform', {
                  duration: theme.transitions.duration.shorter,
                }),
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            />
          </IconButton>
        </Tooltip>
      </Header>
      {description}
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        {/* inputs inside the card must not drag, pan or zoom the canvas */}
        <Box className="nodrag nopan nowheel" sx={{ cursor: 'auto' }}>
          <WorkflowStepForm nodeId={data.nodeId} />
        </Box>
      </Collapse>
      {footer}
    </NodeWrapper>
  );
};

export const BaseNode = memo(BaseNodeComponent);
